import { HttpErrorResponse, HttpParams, HttpStatusCode } from '@angular/common/http';

export type FormValue = string | number | boolean | null | undefined;
export type FormValueOrArray = FormValue | readonly string[];

export interface IBaseResponseModel<T = unknown> {
  statusCode: number;
  statusMessage: string;
  data: T[];
}

/**
 * Standard API response model + shared helpers.
 * - Comments in English (per project rules)
 * - Optimized to avoid unnecessary allocations
 * - Strict-safe normalization and error mapping
 * - Data is always an array (project convention)
 */
export class BaseResponseModel<T = unknown> implements IBaseResponseModel<T> {
  public statusCode = 0;

  public statusMessage = '';

  public data: T[] = [];

  /** Common messages (avoid repeating string literals) */
  private static readonly MSG_INVALID_RESPONSE = 'Respuesta inválida del servidor';
  private static readonly MSG_EMPTY_MESSAGE = 'Respuesta sin mensaje';
  private static readonly MSG_NO_CONNECTION = 'No se puede conectar con el servidor';

  /** Small helper to avoid repeating Object.prototype.hasOwnProperty.call */
  private static readonly hasOwn = Object.prototype.hasOwnProperty;

  /** Type guard for finite numbers */
  private static isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  /** Ensure "data" is always an array */
  private static toArray<T>(value: unknown): T[] {
    return Array.isArray(value) ? (value as T[]) : [value as T];
  }

  /** Create a successful envelope (handy in tests / local defaults) */
  public static ok<T>(data: readonly T[] = [], statusCode = 200, statusMessage = 'OK'): BaseResponseModel<T> {
    const out = new BaseResponseModel<T>();
    out.statusCode = statusCode;
    out.statusMessage = statusMessage;
    out.data = (data as T[]).slice(); // keep immutability from caller
    return out;
  }

  /** Create an error envelope (data is always empty) */
  public static error<T>(message: string, statusCode = 0): BaseResponseModel<T> {
    const out = new BaseResponseModel<T>();
    out.statusCode = statusCode;
    out.statusMessage = message;
    out.data = [];
    return out;
  }

  /**
   * Build x-www-form-urlencoded body from a flat object.
   * - Skips null/undefined values
   * - Converts number/boolean to string
   * - Supports string arrays as repeated keys
   *
   * Performance:
   * - Single pass over keys (no Object.entries/map allocations)
   * - Uses URLSearchParams (fast, minimal overhead)
   * - Returns HttpParams built from the final encoded string
   */
  public static toHttpParams<P extends Record<string, FormValueOrArray>>(payload: P): HttpParams {
    const params = new URLSearchParams();

    for (const key in payload) {
      if (!BaseResponseModel.hasOwn.call(payload, key)) { continue; }

      const value = payload[key];
      if (value == null) { continue; }

      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const v = value[i];
          if (v != null) { params.append(key, String(v)); }
        }
        continue;
      }

      params.set(key, String(value));
    }

    return new HttpParams({ fromString: params.toString() });
  }

  /** One-liner for x-www-form-urlencoded body (for HttpClient POST body) */
  public static toFormUrlEncoded<P extends Record<string, FormValueOrArray>>(payload: P): string {
    return BaseResponseModel.toHttpParams(payload).toString();
  }

  /**
   * Normalize any backend response into a safe BaseResponseModel<T>.
   * Useful when backend might return malformed payloads.
   */
  public static normalize<T>(resp: unknown): BaseResponseModel<T> {
    if (!resp || typeof resp !== 'object') {
      return BaseResponseModel.error<T>(BaseResponseModel.MSG_INVALID_RESPONSE, 0);
    }

    const r = resp as Partial<IBaseResponseModel<T>>;

    const statusCode = BaseResponseModel.isFiniteNumber(r.statusCode) ? r.statusCode : 0;

    const statusMessage =
      (typeof r.statusMessage === 'string' && r.statusMessage.trim().length > 0)
        ? r.statusMessage
        : BaseResponseModel.MSG_EMPTY_MESSAGE;

    const out = new BaseResponseModel<T>();
    out.statusCode = statusCode;
    out.statusMessage = statusMessage;
    out.data = BaseResponseModel.toArray<T>((r as { data?: unknown }).data);
    return out;
  }

  /**
   * Convert HttpErrorResponse / unknown errors into BaseResponseModel<T>.
   * - If backend returned a JSON envelope, it is normalized.
   * - Otherwise returns a safe fallback.
   */
  public static fromError<T>(error: unknown, fallbackMessage: string): BaseResponseModel<T> {
    if (error instanceof HttpErrorResponse) {
      const backend = error.error as unknown;

      // Backend envelope with statusCode/statusMessage/data
      if (backend && typeof backend === 'object') {
        const normalized = BaseResponseModel.normalize<T>(backend);

        // If backend already provided a meaningful envelope, prefer it.
        if (normalized.statusCode !== 0 || normalized.statusMessage !== BaseResponseModel.MSG_EMPTY_MESSAGE) {
          return normalized;
        }
      }

      const status = BaseResponseModel.isFiniteNumber(error.status) ? error.status : 0;

      return BaseResponseModel.error<T>(
        status === 0 ? BaseResponseModel.MSG_NO_CONNECTION : `Error HTTP (${status})`,
        status,
      );
    }

    return BaseResponseModel.error<T>(fallbackMessage, 0);
  }


/* ───────────────────────── Status helpers ───────────────────────── */

  /** True if HTTP status is 2xx */
  public get isOk(): boolean {
    return this.statusCode >= HttpStatusCode.Ok
        && this.statusCode < HttpStatusCode.MultipleChoices;
  }

  /** True if request was accepted but not completed (202) */
  public get isAccepted(): boolean {
    return this.statusCode === HttpStatusCode.Accepted;
  }

  /** True if resource was created (201) */
  public get isCreated(): boolean {
    return this.statusCode === HttpStatusCode.Created;
  }

  /** True if validation error (422) */
  public get isValidationError(): boolean {
    return this.statusCode === HttpStatusCode.UnprocessableEntity;
  }

  /** True if unauthorized or forbidden */
  public get isAuthError(): boolean {
    return this.statusCode === HttpStatusCode.Unauthorized
        || this.statusCode === HttpStatusCode.Forbidden;
  }

  /** True if not found */
  public get isNotFound(): boolean {
    return this.statusCode === HttpStatusCode.NotFound;
  }

  /** True if server-side failure (5xx) */
  public get isServerError(): boolean {
    return this.statusCode >= HttpStatusCode.InternalServerError;
  }

  /** True if network / CORS / connection error */
  public get isNetworkError(): boolean {
    return this.statusCode === 0;
  }

  /** True if request failed (non-2xx) */
  public get isError(): boolean {
    return !this.isOk;
  }

  /** True if response contains data */
  public get hasData(): boolean {
    return this.data.length > 0;
  }


  /* ───────────────────────── Data helpers ───────────────────────── */

  /** First item or null */
  public firstOrNull(): T | null {
    return this.data.length > 0 ? this.data[0] as T : null;
  }


  /* ───────────────────────── Immutable helpers ───────────────────────── */
  
  /** Immutable copy with partial overrides */
  public with(patch: Partial<IBaseResponseModel<T>>): BaseResponseModel<T> {
    const out = new BaseResponseModel<T>();
    out.statusCode = BaseResponseModel.isFiniteNumber(patch.statusCode) ? patch.statusCode : this.statusCode;
    out.statusMessage = (typeof patch.statusMessage === 'string') ? patch.statusMessage : this.statusMessage;
    out.data = Array.isArray(patch.data) ? patch.data : this.data;
    return out;
  }
}
